/**
 * Contract Amendment Dialog
 *
 * Admin dialog to modify an active monthly_target contract mid-cycle.
 * Shows current state, accepts new values, previews charge/credit,
 * and calls the apply-contract-amendment Edge Function.
 */

import { useState, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  AlertTriangle,
  ArrowUpCircle,
  ArrowDownCircle,
  DollarSign,
  Loader2,
  TrendingUp,
  TrendingDown,
  Clock,
} from 'lucide-react';
import { Contract, computeContractMetrics } from '@/contexts/ContractsContext';
import { supabase } from '@/integrations/supabase/client';

interface ContractAmendmentDialogProps {
  contract: Contract | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function ContractAmendmentDialog({ contract, onClose, onSuccess }: ContractAmendmentDialogProps) {
  const [newTotalLeads, setNewTotalLeads] = useState(0);
  const [newPricePerLead, setNewPricePerLead] = useState(0);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Reset form when contract changes
  useEffect(() => {
    if (contract) {
      setNewTotalLeads(contract.totalLeadsCommitted);
      setNewPricePerLead(contract.pricePerLead ?? 0);
      setNotes('');
      setShowConfirmation(false);
    }
  }, [contract]);

  const metrics = useMemo(() => {
    if (!contract) return null;
    return computeContractMetrics(contract);
  }, [contract]);

  // Compute preview
  const preview = useMemo(() => {
    if (!contract) return null;

    const currentPrice = contract.pricePerLead ?? 0;
    const leadsDelta = newTotalLeads - contract.totalLeadsCommitted;
    const leadsChanged = leadsDelta !== 0;
    const priceChanged = newPricePerLead !== currentPrice;
    const nothingChanged = !leadsChanged && !priceChanged;

    let amountCents = 0;
    if (leadsChanged) {
      if (leadsDelta > 0) {
        amountCents = Math.round(leadsDelta * currentPrice * 100);
      } else {
        const removedLeads = Math.abs(leadsDelta);
        const undeliveredBeingRemoved = Math.min(
          removedLeads,
          contract.totalLeadsCommitted - contract.leadsDelivered
        );
        amountCents = -Math.round(undeliveredBeingRemoved * currentPrice * 100);
      }
    }

    const isUpgrade = leadsDelta > 0;
    const isDowngrade = leadsDelta < 0;
    const belowDelivered = newTotalLeads < contract.leadsDelivered;
    const exceedsTwoX = newTotalLeads > contract.totalLeadsCommitted * 2;
    const daysRemaining = metrics?.daysRemaining ?? 0;
    const cycleLessThan7Days = daysRemaining > 0 && daysRemaining < 7;

    // Estimate next invoice to warn about credit > 80%
    const estimatedNextInvoiceCents = Math.round(
      (priceChanged ? newPricePerLead : currentPrice) * newTotalLeads * 100
    );
    const creditExceedsInvoice = amountCents < 0 && Math.abs(amountCents) > estimatedNextInvoiceCents * 0.8;

    return {
      leadsDelta,
      leadsChanged,
      priceChanged,
      nothingChanged,
      amountCents,
      amountDollars: Math.abs(amountCents) / 100,
      isUpgrade,
      isDowngrade,
      belowDelivered,
      exceedsTwoX,
      cycleLessThan7Days,
      daysRemaining,
      creditExceedsInvoice,
      willFulfill: contract.leadsDelivered >= newTotalLeads,
    };
  }, [contract, newTotalLeads, newPricePerLead, metrics]);

  const handleSubmit = async () => {
    if (!contract || !preview) return;

    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error("Authentication required. Please sign in again.");
        return;
      }

      const { data, error } = await supabase.functions.invoke("apply-contract-amendment", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          contractId: contract.id,
          newTotalLeads,
          newPricePerLead,
          notes: notes.trim() || undefined,
        },
      });

      let errorBody: Record<string, unknown> | null = null;
      if (error && typeof error === "object" && "context" in error) {
        try {
          errorBody = await (error as unknown as { context: Response }).context.json();
        } catch { /* ignore */ }
      }

      if (error || !data?.success) {
        const errDetail = errorBody?.detail ?? errorBody?.error ?? data?.error;
        toast.error((typeof errDetail === "string" ? errDetail : null) || "Failed to apply amendment.");
        return;
      }

      const typeLabel = preview.isUpgrade ? "upgraded" : preview.isDowngrade ? "downgraded" : "updated";
      toast.success(`Contract ${typeLabel} successfully`);
      onSuccess();
      onClose();
    } catch (err) {
      console.error("[ContractAmendmentDialog] Error:", err);
      toast.error("Failed to apply amendment. Please try again.");
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  if (!contract || !preview) return null;

  const currentPrice = contract.pricePerLead ?? 0;

  return (
    <Dialog open={!!contract} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Modify Contract
          </DialogTitle>
          <DialogDescription>
            Adjust lead count or price per lead for {contract.agentName || "this agent"}.
            Changes take effect immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 pr-1">
          {/* Current contract summary */}
          <div className="rounded-md border bg-muted/30 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{contract.agentName}</span>
              <Badge variant="secondary">{contract.contractType}</Badge>
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Committed</span>
                <p className="font-medium">{contract.totalLeadsCommitted} leads</p>
              </div>
              <div>
                <span className="text-muted-foreground">Delivered</span>
                <p className="font-medium">{contract.leadsDelivered} leads</p>
              </div>
              <div>
                <span className="text-muted-foreground">Price/Lead</span>
                <p className="font-medium">${currentPrice.toFixed(2)}</p>
              </div>
            </div>
            {metrics && metrics.daysRemaining > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                {metrics.daysRemaining} days remaining in cycle
              </div>
            )}
          </div>

          {/* Cancel-pending warning */}
          {contract.cancelAtPeriodEnd && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                Cancel Pending
              </div>
              <p className="text-xs text-amber-700 mt-1">
                This agent has requested cancellation at cycle end.
                This amendment does not cancel that request.
              </p>
            </div>
          )}

          <Separator />

          {/* New values form */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="newTotalLeads">New Total Leads</Label>
              <Input
                id="newTotalLeads"
                type="number"
                min={contract.leadsDelivered}
                value={newTotalLeads}
                onChange={(e) => setNewTotalLeads(Number(e.target.value))}
              />
              <p className="text-xs text-muted-foreground">
                Min: {contract.leadsDelivered} (already delivered)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPricePerLead">New Price per Lead ($)</Label>
              <Input
                id="newPricePerLead"
                type="number"
                min={0.01}
                step={0.01}
                value={newPricePerLead}
                onChange={(e) => setNewPricePerLead(Number(e.target.value))}
              />
              <p className="text-xs text-muted-foreground">
                Current: ${currentPrice.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="amendmentNotes">Notes (optional)</Label>
            <Textarea
              id="amendmentNotes"
              placeholder="e.g. Client requested upgrade after good results"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
            />
          </div>

          <Separator />

          {/* Preview */}
          {!preview.nothingChanged && (
            <div className="space-y-3">
              <p className="text-sm font-medium">Preview</p>

              {preview.leadsChanged && (
                <div className="flex items-center gap-2 text-sm">
                  {preview.isUpgrade
                    ? <ArrowUpCircle className="h-4 w-4 text-green-600" />
                    : <ArrowDownCircle className="h-4 w-4 text-orange-600" />
                  }
                  <span>
                    Leads: {contract.totalLeadsCommitted} → {newTotalLeads}{' '}
                    <span className={preview.isUpgrade ? "text-green-600 font-medium" : "text-orange-600 font-medium"}>
                      ({preview.isUpgrade ? '+' : ''}{preview.leadsDelta})
                    </span>
                  </span>
                </div>
              )}

              {preview.priceChanged && (
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="h-4 w-4 text-blue-600" />
                  <span>
                    Price: ${currentPrice.toFixed(2)} → ${newPricePerLead.toFixed(2)}
                  </span>
                </div>
              )}

              {preview.amountCents !== 0 && (
                <div className={`rounded-md p-3 ${
                  preview.amountCents > 0
                    ? 'border border-green-200 bg-green-50'
                    : 'border border-orange-200 bg-orange-50'
                }`}>
                  <div className="flex items-center gap-2">
                    {preview.amountCents > 0
                      ? <TrendingUp className="h-4 w-4 text-green-700" />
                      : <TrendingDown className="h-4 w-4 text-orange-700" />
                    }
                    <span className={`text-sm font-medium ${
                      preview.amountCents > 0 ? 'text-green-700' : 'text-orange-700'
                    }`}>
                      {preview.amountCents > 0
                        ? `Charge: $${preview.amountDollars.toFixed(2)} added to next invoice`
                        : `Credit: -$${preview.amountDollars.toFixed(2)} applied to next invoice only`
                      }
                    </span>
                  </div>
                </div>
              )}

              {preview.willFulfill && (
                <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                  <p className="text-sm text-blue-700">
                    Contract will transition to <strong>fulfilled</strong> status (delivered ≥ new target).
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Warnings */}
          {(preview.belowDelivered || preview.exceedsTwoX || preview.cycleLessThan7Days || preview.creditExceedsInvoice) && (
            <div className="space-y-2">
              {preview.belowDelivered && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                    <AlertTriangle className="h-4 w-4" />
                    Cannot set target below delivered count ({contract.leadsDelivered})
                  </div>
                </div>
              )}
              {preview.exceedsTwoX && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
                  <div className="flex items-center gap-2 text-sm text-amber-800">
                    <AlertTriangle className="h-4 w-4" />
                    New target is more than 2x the current value. Please confirm this is intentional.
                  </div>
                </div>
              )}
              {preview.cycleLessThan7Days && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
                  <div className="flex items-center gap-2 text-sm text-amber-800">
                    <Clock className="h-4 w-4" />
                    Only {preview.daysRemaining} days remain in this cycle.
                    Additional leads may not be fully delivered before renewal.
                  </div>
                </div>
              )}
              {preview.creditExceedsInvoice && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
                  <div className="flex items-center gap-2 text-sm text-amber-800">
                    <AlertTriangle className="h-4 w-4" />
                    Credit may exceed the next invoice amount. Excess credit will be lost (Stripe caps invoice at $0).
                    Consider issuing a manual refund for the difference.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Confirmation step */}
          {showConfirmation && !preview.nothingChanged && !preview.belowDelivered && (
            <div className="rounded-md border-2 border-primary/50 bg-primary/5 p-3">
              <p className="text-sm font-medium">
                Are you sure? This will{' '}
                {preview.amountCents > 0
                  ? `charge $${preview.amountDollars.toFixed(2)} on the next invoice`
                  : preview.amountCents < 0
                    ? `apply a -$${preview.amountDollars.toFixed(2)} credit to the next invoice`
                    : 'update the contract'
                }.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                The Stripe billing will be updated immediately. This action cannot be undone.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || preview.nothingChanged || preview.belowDelivered}
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying...</>
            ) : showConfirmation ? (
              'Confirm Amendment'
            ) : (
              'Review & Apply'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
